import React, { useState } from "react";
import { API_URL, getAuthHeaders } from "../utils/api";

const SEVERITY_LABELS = {
  1: "Minor – barely noticeable",
  2: "Low – workaround exists",
  3: "Moderate – somewhat disruptive",
  4: "Notable – affects workflow",
  5: "Medium – regularly impacts work",
  6: "Significant – causes frequent issues",
  7: "High – major disruption",
  8: "Very High – blocks key functionality",
  9: "Critical – prevents core operations",
  10: "Emergency – system down",
};

function BugReportModal({ onClose }) {
  const [form, setForm] = useState({
    location_description: "",
    description: "",
    priority: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.location_description.trim() || !form.description.trim() || !form.priority) {
      setError("Please fill out all fields.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/admin/help-submissions`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ type: "bug", ...form }),
      });
      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError(data.error || "Failed to submit. Please try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content help-submission-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>🐛 Report a Bug</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {submitted ? (
          <div className="modal-body submission-success">
            <div className="submission-success-icon">✅</div>
            <h3>Bug report submitted!</h3>
            <p>Thank you for the report. Our team will review it shortly.</p>
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && <div className="form-error-banner">{error}</div>}

              <div className="form-group">
                <label htmlFor="bug-location">Where is the bug? *</label>
                <input
                  id="bug-location"
                  type="text"
                  placeholder="e.g. Orders page, Customer detail, Invoice modal..."
                  value={form.location_description}
                  onChange={(e) => setForm({ ...form, location_description: e.target.value })}
                  maxLength={500}
                />
              </div>

              <div className="form-group">
                <label htmlFor="bug-description">Describe the bug *</label>
                <textarea
                  id="bug-description"
                  placeholder="What happened? What did you expect to happen? Steps to reproduce..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={7}
                  className="form-textarea-long"
                />
              </div>

              <div className="form-group">
                <label htmlFor="bug-severity">
                  Severity *
                  {form.priority && (
                    <span className="priority-hint"> — {SEVERITY_LABELS[form.priority]}</span>
                  )}
                </label>
                <div className="priority-selector">
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`priority-btn ${parseInt(form.priority) === n ? "selected" : ""} priority-color-${n}`}
                      onClick={() => setForm({ ...form, priority: n })}
                      title={SEVERITY_LABELS[n]}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <div className="priority-scale-labels">
                  <span>1 = Minor</span>
                  <span>10 = Critical</span>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? "Submitting…" : "Submit Bug Report"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default BugReportModal;
